import { GridFilterItem } from "@mui/x-data-grid-pro";
import { getConstant, getTagDisplayValue } from "../../utility/constantsTagsUtils";
import { Constants, ITag } from "../graph/StateTags";
import { CustomGridColDef } from "./gridColumnDefs";
import {RowDef, TagCellType} from "./cells/cellUtils";
import {IGridFilterItem} from "./GridView";

export const noGroup = { id: "none", value: "None" };

type ArraySearch =  (tags: ITag[], callback: (value: ITag) => boolean) => boolean

function tagStringComparison<T = string | string[]>(constants: Constants, rhsValue: T | undefined, arraySearch: ArraySearch,
                                                    stringCmpFunction: (lhs: string, rhs: T) => boolean)
{
    if (rhsValue)
    {
        return (row: RowDef, cell: string) =>
        {
            const tagCell = row[cell] as TagCellType;

            if (Array.isArray(tagCell?.tags))
            {
                return arraySearch(tagCell.tags, (tag) =>
                {
                    if (tag && tag.type !== noGroup.id)
                    {
                        const lhsValue = String(getTagDisplayValue(tag, constants));
                        if (stringCmpFunction(lhsValue, rhsValue))
                        {
                            return true;
                        }
                    }

                    return false;
                })
            }

            return false;
        };
    }
    return null;
}

function tagNumberComparison<T = number | number[]>(rhsValue: T | undefined, arraySearch: ArraySearch,
                                                    numberCmpFunction: (lhs: number, rhs: T) => boolean)
{
    if (rhsValue)
    {
        return (row: RowDef, cell: string) =>
        {
            const tagCell = row[cell] as TagCellType;

            if (Array.isArray(tagCell?.tags))
            {
                return arraySearch(tagCell.tags, (tag) =>
                {
                    return numberCmpFunction(Number(tag.value), rhsValue);
                });
            }

            return false;
        }
    }

    return null;
}

function tagDateComparison(rhsValue: Date, arraySearch: ArraySearch,
                           dateCompare: (lhs: Date, rhs: Date) => boolean)
{
    return (row: RowDef, cell: string) => {
        const tagCell = row[cell] as TagCellType;

        if (Array.isArray(tagCell?.tags)) {
            arraySearch(tagCell.tags, (tag) => dateCompare(new Date(tag.value as number), rhsValue));
        }
        return false;
    }
}
function tagDateFieldComparison(rhsField: string, arraySearch: ArraySearch,
                           dateCompare: (lhs: Date, rhs: Date) => boolean)
{
    return (row: RowDef, cell: string) => {
        const lhsCell = row[cell] as TagCellType;
        const rhsCell = row[rhsField] as TagCellType;

        if (Array.isArray(lhsCell?.tags) && Array.isArray(rhsCell?.tags)) {
            return dateCompare(new Date(lhsCell.tags[0].value as number), new Date(rhsCell.tags[0].value as number));
        }
        return false;
    }
}
function tagsEmpty(row: RowDef, cell: string)
{
    const tagCell = row[cell] as TagCellType;

    if (Array.isArray(tagCell?.tags)) {
        return tagCell.tags.length === 0;
    }
    return true;
}

function tagsNotEmpty(row: RowDef, cell: string)
{
    return !tagsEmpty(row, cell);
}

type TagFilter = (row: RowDef, cell: string) => boolean
export function createTagFilter(filterItem: GridFilterItem, constants: Constants) : TagFilter | null
{
    const constant = getConstant(filterItem.columnField, constants);

    if (constant === undefined)
    {
        return null;
    }
    else if (constant.type === "string")
    {
        switch (filterItem.operatorValue)
        {
            case "contains":
                return tagStringComparison(constants,
                    filterItem.value,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs.includes(rhs));
            case "doesNotContain":
                return (tags, tag): boolean => {
                    // No tags means that they do not contain anything
                    if (! tags[tag]) return true;

                    const compare = tagStringComparison(constants, filterItem.value,
                        (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                        (lhs, rhs) => lhs.includes(rhs));
                    
                    return compare != null && ! compare(tags, tag);
                }
            case "equals":
                return tagStringComparison(constants,
                    filterItem.value,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs === rhs);
            case "startsWith":
                return tagStringComparison(constants,
                    filterItem.value,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs.startsWith(rhs));
            case "endsWith":
                return tagStringComparison(constants,
                    filterItem.value,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs.endsWith(rhs));
            case "isAnyOf":
                const anyOfValues = filterItem.value as string[];
                return tagStringComparison(constants,
                    anyOfValues,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => rhs.includes(lhs));
            case "isEmpty":
                return tagsEmpty;
            case "isNotEmpty":
                return tagsNotEmpty;
        }
    }
    else if (constant.type === "boolean")
    {
        if (filterItem.operatorValue === "is")
        {
            if (filterItem.value)
            {
                const value = filterItem.value === "true";
                return (row: RowDef, cell: string) =>
                {
                    const tagCell = row[cell] as TagCellType;

                    if (Array.isArray(tagCell?.tags)) {
                        return tagCell.tags.some((tag: ITag) => tag.value === value);
                    }
                    return false;
                };
            }
        }
    }
    else if (constant.type === "number")
    {
        const filterValueNumber = Number(filterItem.value);
        switch (filterItem.operatorValue)
        {
            case "=":
                return tagNumberComparison(filterValueNumber,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs === rhs);
            case "!=":
                return tagNumberComparison(filterValueNumber,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.every(callback),
                    (lhs, rhs) => lhs !== rhs);
            case ">":
                return tagNumberComparison(filterValueNumber,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs > rhs);
            case "<":
                return tagNumberComparison(filterValueNumber,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs < rhs);
            case "<=":
                return tagNumberComparison(filterValueNumber,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs <= rhs);
            case ">=":
                return tagNumberComparison(filterValueNumber,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => lhs >= rhs);
            case "isAnyOf":
                const anyOfValues = (filterItem.value as string[] ?? []).map(Number);
                return tagNumberComparison(anyOfValues,
                    (tags: ITag[], callback: (value: ITag) => boolean) => tags.some(callback),
                    (lhs, rhs) => rhs.includes(lhs));
            case "isEmpty":
                return tagsEmpty;
            case "isNotEmpty":
                return tagsNotEmpty;
        }
    }
    else if (constant.type === "date") {
        const filterValueDate = new Date(filterItem.value);

        switch (filterItem.operatorValue)
        {
            case "is":
                return tagDateComparison(filterValueDate, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs === rhs);
            case "not":
                return tagDateComparison(filterValueDate, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs !== rhs);
            case "after":
                return tagDateComparison(filterValueDate, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs > rhs);
            case "onOrAfter":
                return tagDateComparison(filterValueDate, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs >= rhs);
            case "before":
                return tagDateComparison(filterValueDate, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs < rhs);
            case "onOrBefore":
                return tagDateComparison(filterValueDate, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs <= rhs);
            case "afterColumn":
                return tagDateFieldComparison(filterItem.value, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs >= rhs);
            case "beforeColumn":
                return tagDateFieldComparison(filterItem.value, (tags, callback) => tags?.some(callback),
                    (lhs, rhs) => lhs <= rhs);
        }
    }

    return null;
}

function defaultCompare<F extends string | number, T extends string | string[] | number | number[]>(filterValue: T | undefined, compareFunction: (value: F, filterValue: T) => boolean)
{
    if (filterValue !== undefined && (!Array.isArray(filterValue) || filterValue.length > 0))
    {
        return (value?: F) => value !== undefined && compareFunction(value, filterValue);
    }

    return null;
}

export function createFilter(filterItem: IGridFilterItem, column: CustomGridColDef)
{
    const includes = (value: string, compare: string) => filterItem.caseSensitive ?
        value.includes(compare) : value.toLowerCase().includes(compare.toLowerCase());
    const equals = (value: string, compare: string) => filterItem.caseSensitive ?
        value === compare : value.toLowerCase() === compare.toLowerCase();
    const startsWith = (value: string, compare: string) => filterItem.caseSensitive ?
        value.startsWith(compare) : value.toLowerCase().startsWith(compare.toLowerCase());
    const endsWith = (value: string, compare: string) => filterItem.caseSensitive ?
        value.endsWith(compare) : value.toLowerCase().endsWith(compare.toLowerCase());

    if (column.type === "string")
    {
        switch (filterItem.operatorValue)
        {
            case "contains":
                return defaultCompare(filterItem.value, (value: string, filterValue: string) =>
                    includes(value, filterValue));
            case "doesNotContain":
                return defaultCompare(filterItem.value, (value: string, filterValue: string) =>
                    ! includes(value, filterValue));
            case "equals":
                return defaultCompare(filterItem.value, (value: string, filterValue: string) =>
                    equals(value, filterValue));
            case "startsWith":
                return defaultCompare(filterItem.value, (value: string, filterValue: string) =>
                    startsWith(value, filterValue));
            case "endsWith":
                return defaultCompare(filterItem.value, (value: string, filterValue: string) =>
                    endsWith(value, filterValue));
            case "isAnyOf":
                return defaultCompare(filterItem.value as string[], (value: string, filterValue: string[]) =>
                    filterValue.some && filterValue.some(filterValue => equals(value, filterValue)));
            case "isEmpty":
                return (value?: string) => !Boolean(value);
            case "isNotEmpty":
                return (value?: string) => Boolean(value);
        }
    }
    else if (column.type === "boolean")
    {
        if (filterItem.operatorValue === "is")
        {
            if (filterItem.value !== undefined)
            {
                const filterValue = filterItem.value === "true";
                return (value: boolean) =>
                {
                    return value === filterValue;
                };
            }
        }
    }
    else if (column.type === "number")
    {
        const filterValue = Number(filterItem.value);
        switch (filterItem.operatorValue)
        {
            case "=":
                return defaultCompare(filterValue, (value: number, filterValue: number) => value === filterValue);
            case "!=":
                return defaultCompare(filterValue, (value: number, filterValue: number) => value !== filterValue);
            case ">":
                return defaultCompare(filterValue, (value: number, filterValue: number) => value > filterValue);
            case "<":
                return defaultCompare(filterValue, (value: number, filterValue: number) => value < filterValue);
            case "<=":
                return defaultCompare(filterValue, (value: number, filterValue: number) => value <= filterValue);
            case ">=":
                return defaultCompare(filterValue, (value: number, filterValue: number) => value >= filterValue);
            case "isAnyOf":
                return defaultCompare((filterItem.value as string[]).map(Number), (value: number, filterValue: number[]) => filterValue.includes(value));
            case "isEmpty":
                return (value?: number) => typeof value !== "number";
            case "isNotEmpty":
                return (value?: number) => typeof value === "number";
        }
    }
    else if (column.type === "singleSelect")
    {
        const filterValue = String(filterItem.value);
        switch (filterItem.operatorValue)
        {
            case "is":
                return defaultCompare(filterValue, (value: string, filterValue: string) => String(value) === filterValue);
            case "isNot":
                return defaultCompare(filterValue, (value: string, filterValue: string) => String(value) !== filterValue);
            case "isAnyOf":
                return defaultCompare((filterItem.value as string[]), (value: string, filterValue: string[]) => filterValue.includes(String(value)));
        }
    }

    return null;
}